// src/containers/BookingRequest/components/PropertySummaryCard.tsx
import React from "react";
import { IoLocationOutline } from "react-icons/io5";
import { FaRegUser } from "react-icons/fa";
import { CalendarIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import moment from "moment";
import GallerySlider from "components/GallerySlider/GallerySlider";
import { currencySymbol } from "shared/CurrencySymbol";

interface Property {
  title?: string;
  slug?: string;
  neighborhood?: string;
  address?: string;
  city?: string;
  state?: string;
  place_images?: any[];
  rooms_count?: number;
  minimum_months_of_booking?: number;
}

interface LineItem {
  room_id?: string;
}

interface Expense {
  category: string;
  amount: number;
}

interface Discount {
  description: string;
  amount: number;
}

interface PropertySummaryCardProps {
  property: Property;
  bookingRequestDetails: any;
  isSharedProperty: boolean;
  checkInDate: Date;
  checkOutDate: Date;
  bookingStatus: string;
  currentUser?: any;
}

const PriceRow: React.FC<{
  label: React.ReactNode;
  value: string;
  valueClass?: string;
}> = ({ label, value, valueClass = "text-gray-900 dark:text-white" }) => (
  <div className="flex justify-between items-start">
    <span className="text-gray-600 dark:text-gray-400">{label}</span>
    <span className={`font-semibold ${valueClass}`}>{value}</span>
  </div>
);

const PropertySummaryCard: React.FC<PropertySummaryCardProps> = ({
  property,
  bookingRequestDetails,
  isSharedProperty,
  checkInDate,
  checkOutDate,
  bookingStatus,
  currentUser,
}) => {
  const isConfirmed = bookingStatus === "confirmed";
  const isHost = currentUser?.is_host || false;

  const sortAddress = [property?.neighborhood, property?.city, property?.state]
    .filter(Boolean)
    .join(", ");
  const displayAddress = isConfirmed ? property?.address : sortAddress;

  const noOfMonths = bookingRequestDetails?.no_of_months;
  const minimumMonths = property?.minimum_months_of_booking;
  const isBelowMinimum =
    minimumMonths != null &&
    noOfMonths != null &&
    Number(noOfMonths) < Number(minimumMonths);

  const roomIds =
    bookingRequestDetails?.line_items?.map((item: LineItem) => item.room_id) ||
    [];

  const href = isSharedProperty
    ? `/listing-stay-detail/${property?.slug}?room=${roomIds.join(",")}`
    : `/listing-stay-detail/${property?.slug}`;

  // Price values
  const itemTotal = Number(bookingRequestDetails?.item_total || 0);
  const serviceTax = Number(bookingRequestDetails?.service_tax || 0);
  const extraGuestAmount = Number(bookingRequestDetails?.extra_guest_amount || 0);
  const taxTotal = Number(bookingRequestDetails?.tax_total || 0);
  const totalAmount = Number(bookingRequestDetails?.total_amount || 0);

  // Host payout = total minus service fee
  const hostPayout = totalAmount - serviceTax;

  const belowMinimumMessage = isHost
    ? `The guest's booking is ${noOfMonths} ${Number(noOfMonths) === 1 ? "month" : "months"}, which is below your minimum required stay of ${minimumMonths} ${minimumMonths === 1 ? "month" : "months"}. You may want to discuss this with the guest before confirming.`
    : `Your booking is ${noOfMonths} ${Number(noOfMonths) === 1 ? "month" : "months"}, which is below the minimum required stay of ${minimumMonths} ${minimumMonths === 1 ? "month" : "months"}. Please adjust your dates to meet the requirement.`;

  const modifiedStatus = (PropertyStatus: string | undefined | boolean) => {
    const status =
      typeof PropertyStatus === "boolean"
        ? PropertyStatus.toString()
        : PropertyStatus;
    switch (status) {
      case "true":
        return "Shared Property";
      case "false":
        return "Entire Property";
      default:
        return "";
    }
  };

  const renderSliderGallery = () => (
    <div className="w-full relative">
      <GallerySlider
        uniqueID={`PropertyCard_${bookingRequestDetails?.id}`}
        ratioClass="aspect-w-4 aspect-h-3"
        galleryImgs={property?.place_images || []}
        href={href}
      />
      <div className="absolute left-3 top-3 z-[1] bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg text-white text-xs font-semibold">
        {modifiedStatus(isSharedProperty)}
      </div>
    </div>
  );

  const renderGuestPriceBreakdown = () => (
    <div className="space-y-3">
      <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        Price Breakdown
      </h3>

      <div className="space-y-2 text-sm">
        {itemTotal > 0 && (
          <PriceRow
            label={
              <span className="flex items-start gap-1">
                First Month's Rent
                <span className="cursor-help" data-tooltip-id="rent-tooltip" data-tooltip-content="This deposit will be transferred to host 24 hours after your check-in to the property.">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </span>
                <Tooltip id="rent-tooltip" place="top" style={{ backgroundColor: "#f97316", color: "white", borderRadius: "0.5rem", padding: "0.5rem", maxWidth: "200px", fontSize: "0.75rem", zIndex: 9999 }} />
              </span>
            }
            value={`${currencySymbol()} ${itemTotal.toFixed(2)}`}
          />
        )}

        {serviceTax > 0 && (
          <PriceRow
            label={
              <span className="flex items-start gap-1">
                One-Time Service Fee
                <span className="cursor-help" data-tooltip-id="service-tooltip" data-tooltip-content="This is a one-time fee that allows us to provide assistance and customer support. Furthermore, secure your payment and guarantee your accommodation.">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </span>
                <Tooltip id="service-tooltip" place="top" style={{ backgroundColor: "#f97316", color: "white", borderRadius: "0.5rem", padding: "0.5rem", maxWidth: "200px", fontSize: "0.75rem", zIndex: 9999 }} />
              </span>
            }
            value={`${currencySymbol()} ${serviceTax.toFixed(2)}`}
          />
        )}

        {extraGuestAmount > 0 && (
          <PriceRow label="Extra Guest Amount" value={`${currencySymbol()} ${extraGuestAmount.toFixed(2)}`} />
        )}

        {taxTotal > 0 && (
          <PriceRow label="Total Tax" value={`${currencySymbol()} ${taxTotal.toFixed(2)}`} />
        )}

        {bookingRequestDetails?.invoice?.expenses?.map((expense: Expense, index: number) => (
          <PriceRow key={index} label={<span className="capitalize">{expense.category}</span>} value={`${currencySymbol()} ${Number(expense.amount).toFixed(2)}`} />
        ))}

        {bookingRequestDetails?.invoice?.discounts?.map((discount: Discount, index: number) => (
          <PriceRow key={`discount-${index}`} label={<span className="capitalize">{discount.description}</span>} value={`- ${currencySymbol()} ${Number(discount.amount).toFixed(2)}`} valueClass="text-red-500 dark:text-red-400" />
        ))}
      </div>

      {bookingRequestDetails?.total_amount && (
        <div className="pt-4 border-t-2 border-orange-500 dark:border-orange-600">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-gray-900 dark:text-white">Total Amount</span>
            <span className="text-xl font-bold text-orange-600 dark:text-orange-500">
              {currencySymbol()} {totalAmount.toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const renderHostPriceBreakdown = () => (
    <div className="space-y-4">
      <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        Booking Summary
      </h3>

      {/* Guest Pays section */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-2 text-sm">
        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Guest Pays
        </p>

        {itemTotal > 0 && (
          <PriceRow label="First Month's Rent" value={`${currencySymbol()} ${itemTotal.toFixed(2)}`} />
        )}
        {serviceTax > 0 && (
          <PriceRow label="Service Fee (Nomadroof)" value={`${currencySymbol()} ${serviceTax.toFixed(2)}`} valueClass="text-gray-500 dark:text-gray-400" />
        )}
        {extraGuestAmount > 0 && (
          <PriceRow label="Extra Guest Amount" value={`${currencySymbol()} ${extraGuestAmount.toFixed(2)}`} />
        )}
        {taxTotal > 0 && (
          <PriceRow label="Total Tax" value={`${currencySymbol()} ${taxTotal.toFixed(2)}`} />
        )}
        {bookingRequestDetails?.invoice?.expenses?.map((expense: Expense, index: number) => (
          <PriceRow key={index} label={<span className="capitalize">{expense.category}</span>} value={`${currencySymbol()} ${Number(expense.amount).toFixed(2)}`} />
        ))}
        {bookingRequestDetails?.invoice?.discounts?.map((discount: Discount, index: number) => (
          <PriceRow key={`discount-${index}`} label={<span className="capitalize">{discount.description}</span>} value={`- ${currencySymbol()} ${Number(discount.amount).toFixed(2)}`} valueClass="text-red-500 dark:text-red-400" />
        ))}

        <div className="pt-2 border-t border-gray-200 dark:border-gray-600 flex justify-between items-center">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Guest Total</span>
          <span className="text-sm font-bold text-gray-900 dark:text-white">
            {currencySymbol()} {totalAmount.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Host Payout section */}
      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800 space-y-2 text-sm">
        <p className="text-xs font-bold text-green-700 dark:text-green-400 uppercase tracking-wide mb-3">
          Your Earnings
        </p>

        {itemTotal > 0 && (
          <PriceRow label="First Month's Rent" value={`${currencySymbol()} ${itemTotal.toFixed(2)}`} />
        )}
        {extraGuestAmount > 0 && (
          <PriceRow label="Extra Guest Amount" value={`${currencySymbol()} ${extraGuestAmount.toFixed(2)}`} />
        )}
        {serviceTax > 0 && (
          <div className="flex justify-between items-start text-xs text-gray-500 dark:text-gray-400 italic">
            <span>Service Fee (kept by Nomadroof)</span>
            <span>- {currencySymbol()} {serviceTax.toFixed(2)}</span>
          </div>
        )}

        <div className="pt-2 border-t border-green-300 dark:border-green-700 flex justify-between items-center">
          <span className="text-sm font-bold text-green-800 dark:text-green-300">You Receive</span>
          <span className="text-lg font-bold text-green-700 dark:text-green-400">
            {currencySymbol()} {hostPayout.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Property Image Gallery */}
      <div className="relative">{renderSliderGallery()}</div>

      {/* Property Information */}
      <div className="p-4 sm:p-6 space-y-4">
        {/* Title and Location */}
        <div className="space-y-2">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white line-clamp-2 leading-tight">
            {property?.title}
          </h2>

          {property?.neighborhood && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-full">
              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-xs font-semibold text-orange-700 dark:text-orange-300">
                {property.neighborhood}
              </span>
            </div>
          )}
          <div className="flex gap-2 items-start text-gray-600 dark:text-gray-400">
            <IoLocationOutline className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm line-clamp-2 flex-1">{displayAddress}</p>
          </div>
        </div>

        <hr className="border-gray-200 dark:border-gray-700" />

        {/* Booking Details */}
        <div className="space-y-3">
          {/* Room/Property Type */}
          {isSharedProperty ? (
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
              <p className="text-sm font-semibold text-orange-900 dark:text-orange-300">
                <span className="font-bold">Room:</span>{" "}
                {bookingRequestDetails?.booked_room || "N/A"}
              </p>
            </div>
          ) : (
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Rooms</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {property?.rooms_count || "N/A"}
              </span>
            </div>
          )}

          {/* Dates */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 text-blue-900 dark:text-blue-300">
              <CalendarIcon className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-semibold">
                {moment(checkInDate).format("MMM DD, YYYY")} →{" "}
                {moment(checkOutDate).format("MMM DD, YYYY")}
              </p>
            </div>
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Duration and Guests */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Duration</span>
              </div>
              <p className="text-base font-bold text-gray-900 dark:text-white">
                {noOfMonths || "1"} {Number(noOfMonths) === 1 ? "month" : "months"}
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <FaRegUser className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Guests</span>
              </div>
              <p className="text-base font-bold text-gray-900 dark:text-white">
                {bookingRequestDetails?.number_of_guests}
              </p>
            </div>
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Minimum Stay Warning */}
          {minimumMonths != null && (
            <div className={`rounded-lg p-3 border flex items-start gap-3 ${
              isBelowMinimum
                ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
            }`}>
              <div className="flex-shrink-0 mt-0.5">
                {isBelowMinimum ? (
                  <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="text-xs leading-relaxed">
                <p className={`font-semibold mb-0.5 ${isBelowMinimum ? "text-red-700 dark:text-red-300" : "text-green-700 dark:text-green-300"}`}>
                  Minimum Stay: {minimumMonths} {minimumMonths === 1 ? "month" : "months"}
                </p>
                {isBelowMinimum && (
                  <p className="text-red-600 dark:text-red-400">{belowMinimumMessage}</p>
                )}
              </div>
            </div>
          )}

          {/* Price Breakdown — role-aware */}
          {isHost ? renderHostPriceBreakdown() : renderGuestPriceBreakdown()}
        </div>
      </div>
    </div>
  );
};

export default PropertySummaryCard;